import { useMemo, lazy, Suspense } from 'react';

import { Routes, Route, Navigate, useLocation } from 'react-router';

import { getEnabledMenuItems, getEnabledMenuItemsByRegion } from '../config/menu.config';
import { ErrorBoundary } from '../shared/components/ErrorBoundary';
import { useRegion } from '../shared/hooks/useRegion';

// Lazy load SDK components for better code splitting
const HwpContentView = lazy(() =>
  import('../features/hwp-sdk/views/HwpContentView').then(m => ({ default: m.HwpContentView })),
);
const HcellContentView = lazy(() =>
  import('../features/hcell-sdk/views/HcellContentView').then(m => ({ default: m.HcellContentView })),
);
const DocDiffContentView = lazy(() =>
  import('../features/docdiff-sdk/views/DocDiffContentView').then(m => ({
    default: m.DocDiffContentView,
  })),
);
const OcrContentView = lazy(() =>
  import('../features/ocr-sdk/views/OcrContentView').then(m => ({ default: m.OcrContentView })),
);
const DataloaderContentView = lazy(() =>
  import('../features/dataloader/views/DataloaderContentView').then(m => ({
    default: m.DataloaderContentView,
  })),
);
const LicenseContentView = lazy(() =>
  import('../features/license/views/LicenseContentView').then(m => ({ default: m.LicenseContentView })),
);

// Loading fallback component
const RouteLoadingFallback = () => (
  <div className='flex h-full items-center justify-center bg-gray-50'>
    <div className='text-center'>
      <div className='mb-4'>
        <div className='inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent'></div>
      </div>
      <p className='text-gray-600'>Loading SDK...</p>
    </div>
  </div>
);

export const AppRoutes = () => {
  const { isOverseas, isLoading, regionInfo } = useRegion();
  const location = useLocation();

  // Constants
  const SPREADSHEET_SDK_PATH = '/spreadsheet-sdk';

  // Memoize menu items to prevent recalculation on every render
  const enabledMenuItems = useMemo(() => getEnabledMenuItems(), []);
  const regionFilteredMenuItems = useMemo(() => getEnabledMenuItemsByRegion(isOverseas), [isOverseas]);

  // Memoize component map
  const componentMap: { [key: string]: React.ComponentType } = useMemo(
    () => ({
      hwp: HwpContentView,
      hcell: HcellContentView,
      docdiff: DocDiffContentView,
      ocr: OcrContentView,
      dataloader: DataloaderContentView,
    }),
    [],
  );

  // 지역별로 필터링된 메뉴 중 첫 번째 항목을 기본 경로로 설정
  // 로딩 중일 때는 해외향 기본 경로를 사용하여 깜빡임 방지
  const defaultPath = useMemo(() => {
    if (isLoading) {
      return SPREADSHEET_SDK_PATH; // 로딩 중에는 해외향 기본값 사용
    }
    return regionFilteredMenuItems[0]?.path ?? SPREADSHEET_SDK_PATH;
  }, [isLoading, regionFilteredMenuItems]);

  // 해외 사용자를 위한 경로 매핑 생성 (EN/JA 접두사 포함)
  const overseasRoutes = useMemo(
    () =>
      isOverseas
        ? [
            { path: SPREADSHEET_SDK_PATH, component: HcellContentView },
            { path: `/en${SPREADSHEET_SDK_PATH}`, component: HcellContentView },
            { path: `/ja${SPREADSHEET_SDK_PATH}`, component: HcellContentView },
          ]
        : [],
    [isOverseas],
  );

  // 지역 정보 로딩 중일 때는 리다이렉션하지 않음
  const shouldRedirect = !isLoading && regionInfo;

  // 해외 사용자가 국내향 /en/* 경로에 있을 경우 overseas 기본 경로로 리다이렉트
  if (shouldRedirect && isOverseas && location.pathname.startsWith('/en')) {
    return <Navigate to={defaultPath} replace />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route
            path='/'
            element={shouldRedirect ? <Navigate to={defaultPath} replace /> : <RouteLoadingFallback />}
          />

          {/* 기본 라우트 */}
          {enabledMenuItems.map(menu => {
            const Component = componentMap[menu.id];
            return Component ? <Route key={menu.path} path={menu.path} element={<Component />} /> : null;
          })}

          {/* 해외 사용자를 위한 추가 라우트 */}
          {overseasRoutes.map(route => (
            <Route key={route.path} path={route.path} element={<route.component />} />
          ))}

          {/* 라이선스 페이지 */}
          <Route path='/license' element={<LicenseContentView />} />
          <Route path='/en/license' element={<LicenseContentView />} />
          <Route path='/ja/license' element={<LicenseContentView />} />

          {/* 국내향 영문 (/en/*) 라우트 */}
          <Route
            path='/en'
            element={
              shouldRedirect ? <Navigate to={`/en${defaultPath}`} replace /> : <RouteLoadingFallback />
            }
          />
          {enabledMenuItems.map(menu => {
            const Component = componentMap[menu.id];
            return Component ? (
              <Route key={`/en${menu.path}`} path={`/en${menu.path}`} element={<Component />} />
            ) : null;
          })}

          {/* 해외향 일본어 (/ja/*) 라우트 */}
          <Route
            path='/ja'
            element={
              shouldRedirect ? <Navigate to={`/ja${defaultPath}`} replace /> : <RouteLoadingFallback />
            }
          />
          {enabledMenuItems.map(menu => {
            const Component = componentMap[menu.id];
            return Component ? (
              <Route key={`/ja${menu.path}`} path={`/ja${menu.path}`} element={<Component />} />
            ) : null;
          })}
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;
